import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";
import { hasPermission } from "../../../utils/hasPermissions";
import axios from "axios";
export default function StudentAdmissionSummary() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Student report specific filters
  const [batches, setBatches] = useState([]);
  const [branches, setBranches] = useState([]);
  const [admissionTypes] = useState(["REGULAR", "LATERAL", "TRANSFER", "REJOIN"]);
  const [studentStatuses] = useState(["ACTIVE", "PASSED OUT", "DETAINED", "TCOUT"]);
  const [enrollStatuses] = useState(["ADMITTED", "ENROLLED", "DROPPED"]);
  const [workflowStatuses] = useState(["DRAFT", "APPROVED", "INACTIVE"]);

  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedAdmissionTypes, setSelectedAdmissionTypes] = useState([]);
  const [selectedStudentStatuses, setSelectedStudentStatuses] = useState([]);
  const [selectedEnrollStatuses, setSelectedEnrollStatuses] = useState([]);
  const [selectedWorkflowStatuses, setSelectedWorkflowStatuses] = useState([]);

  // State variables
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  // Filter popover state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  // Get user data from session storage (memoized to prevent infinite loops)
  const user = useMemo(() => {
    const userData = sessionStorage.getItem("user");
    const parsedUser = userData ? JSON.parse(userData) : null;
    return parsedUser;
  }, []);

  // Helper functions
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Fetch admitted batches for filter (supports branch filtering)
  const fetchBatches = useCallback(async () => {
    if (!user) return;
    try {
      const branchQuery = selectedBranch ? `?branchId=${selectedBranch}` : "";
      const url = `${API_BASE_URL}/api/admittedbatches/${user.client_id}/${user.college_id}${branchQuery}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });
      if (response.data) {
        if (Array.isArray(response.data)) setBatches(response.data);
        else if (response.data.data && Array.isArray(response.data.data))
          setBatches(response.data.data);
        else if (response.data.items && Array.isArray(response.data.items))
          setBatches(response.data.items);
        else setBatches([]);
      } else {
        setBatches([]);
      }
    } catch (err) {
      console.error("Error fetching batches", err);
      setBatches([]);
    }
  }, [user, selectedBranch]);

  // Fetch branches for filter
  const fetchBranches = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/branches/${user.client_id}/${user.college_id}`,
        { headers: getAuthHeaders() }
      );
      if (response.data && response.data.success && Array.isArray(response.data.data))
        setBranches(response.data.data);
      else setBranches([]);
    } catch (err) {
      console.error("Error fetching branches", err);
      setBranches([]);
    }
  }, [user]);

  // Fetch student report data
  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search && search.trim() !== "") params.append("search", search.trim());
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedAdmissionTypes && selectedAdmissionTypes.length > 0)
        params.append("admissionType", selectedAdmissionTypes.join(","));
      if (selectedStudentStatuses && selectedStudentStatuses.length > 0)
        params.append("studentStatus", selectedStudentStatuses.join(","));
      if (selectedEnrollStatuses && selectedEnrollStatuses.length > 0)
        params.append("enrollStatus", selectedEnrollStatuses.join(","));
      if (selectedWorkflowStatuses && selectedWorkflowStatuses.length > 0)
        params.append("workflowStatus", selectedWorkflowStatuses.join(","));

      const queryString = params.toString();
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const response = await axios.get(url, { headers: getAuthHeaders() });

      let data = [];
      if (Array.isArray(response.data)) data = response.data;
      else if (response.data && Array.isArray(response.data.data)) data = response.data.data;

      const formattedData = data.map((item) => ({ id: item.student_id || item.id, ...item }));
      setAllRows(formattedData);
      setRows(formattedData);
    } catch (error) {
      console.error("Error fetching student report data:", error);
      showSnackbar("Error fetching student report data", "error");
    } finally {
      setLoading(false);
    }
  }, [
    user,
    search,
    selectedBatch,
    selectedBranch,
    selectedAdmissionTypes,
    selectedStudentStatuses,
    selectedEnrollStatuses,
    selectedWorkflowStatuses,
  ]);

  // Menu handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // (Staff management UI removed) — keep the component focused on student reports only

  // Student Report: CSV Download (uses student-specific filters)
  const handleDownloadCsvReport = async () => {
    try {
      const params = new URLSearchParams();
      if (search && search.trim() !== "") params.append("search", search.trim());
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedAdmissionTypes && selectedAdmissionTypes.length > 0)
        params.append("admissionType", selectedAdmissionTypes.join(","));
      if (selectedStudentStatuses && selectedStudentStatuses.length > 0)
        params.append("studentStatus", selectedStudentStatuses.join(","));
      if (selectedEnrollStatuses && selectedEnrollStatuses.length > 0)
        params.append("enrollStatus", selectedEnrollStatuses.join(","));
      if (selectedWorkflowStatuses && selectedWorkflowStatuses.length > 0)
        params.append("workflowStatus", selectedWorkflowStatuses.join(","));

      const queryString = params.toString();
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}/download/csv`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "student_admission_summary_full.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      showSnackbar("CSV downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading student CSV:", error);
      // Fallback: generate CSV from current rows
      generateCsvFromStudentData();
    }
  };

  const generateCsvFromStudentData = () => {
    try {
      // Minimal safe set of columns from student object
      const csvHeaders = [
        "student_id",
        "admission_number",
        "student_name",
        "admitted_batch",
        "branch",
        "admission_type",
        "student_status",
        "enroll_status",
        "email",
        "mobile",
      ];

      const csvData = rows.map((r) => [
        r.student_id || "",
        r.admission_number || "",
        r.student_name || "",
        r.admitted_batch_name || r.admitted_batch || "",
        r.branch_name || r.branch || "",
        r.admission_type || "",
        r.student_status || "",
        r.enroll_status || "",
        r.email || "",
        r.mobile || "",
      ]);

      const csvContent = [
        csvHeaders.join(","),
        ...csvData.map((row) =>
          row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "student_admission_summary_fallback.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar("CSV downloaded successfully (fallback)", "success");
    } catch (error) {
      console.error("Error generating student CSV:", error);
      showSnackbar("Error downloading CSV", "error");
    }
  };

  // Student Report: PDF Download (condensed PDF)
  const handleDownloadPdfReport = async () => {
    try {
      const params = new URLSearchParams();
      if (search && search.trim() !== "") params.append("search", search.trim());
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedAdmissionTypes && selectedAdmissionTypes.length > 0)
        params.append("admissionType", selectedAdmissionTypes.join(","));
      if (selectedStudentStatuses && selectedStudentStatuses.length > 0)
        params.append("studentStatus", selectedStudentStatuses.join(","));
      if (selectedEnrollStatuses && selectedEnrollStatuses.length > 0)
        params.append("enrollStatus", selectedEnrollStatuses.join(","));
      if (selectedWorkflowStatuses && selectedWorkflowStatuses.length > 0)
        params.append("workflowStatus", selectedWorkflowStatuses.join(","));

      const queryString = params.toString();
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}/download/pdf`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "student_admission_summary.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      showSnackbar("PDF downloaded successfully", "success");
    } catch (error) {
      console.error("Error downloading student PDF:", error);
      showSnackbar("Error downloading PDF. Please try again.", "error");
    }
  };

  // Student enrollment health handlers removed

  // Transfer / Rejoin CSV/PDF handlers
  const handleDownloadTransferCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      const queryString = params.toString();
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}/download/transfer/csv`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const response = await axios.get(url, { headers: getAuthHeaders(), responseType: "blob" });
      const blob = new Blob([response.data], { type: "text/csv" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "student_transfer_rejoin.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("Transfer/Rejoin CSV downloaded", "success");
    } catch (err) {
      console.error("Error downloading transfer CSV", err);
      showSnackbar("Error downloading transfer CSV", "error");
    }
  };

  const handleDownloadTransferPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBranch) params.append("branchId", selectedBranch);
      if (selectedBatch) params.append("admittedBatchId", selectedBatch);
      const queryString = params.toString();
      const baseUrl = `${API_BASE_URL}/api/student-reports/${user.client_id}/${user.college_id}/download/transfer/pdf`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const response = await axios.get(url, { headers: getAuthHeaders(), responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "student_transfer_rejoin.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("Transfer/Rejoin PDF downloaded", "success");
    } catch (err) {
      console.error("Error downloading transfer PDF", err);
      showSnackbar("Error downloading transfer PDF", "error");
    }
  };

  // Effects
  useEffect(() => {
    if (user) {
      fetchData();
      fetchBranches();
    }
  }, [user, fetchData, fetchBranches]);

  // Whenever selectedBranch changes, reload admitted batches mapped to that branch
  useEffect(() => {
    if (user) fetchBatches();
  }, [user, selectedBranch, fetchBatches]);

  // Local search and filter logic
  useEffect(() => {
    let filtered = [...allRows];

    // Apply search filter (student fields)
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          (r.student_name && r.student_name.toLowerCase().includes(s)) ||
          (r.admission_no && r.admission_no.toLowerCase().includes(s)) ||
          (r.student_rollno && r.student_rollno.toLowerCase().includes(s)) ||
          (r.email && r.email.toLowerCase().includes(s)) ||
          (r.mobile && r.mobile.toLowerCase().includes(s)) ||
          (r.branch?.branch_name && r.branch.branch_name.toLowerCase().includes(s)) ||
          (r.batch?.admittedbatch && r.batch.admittedbatch.toLowerCase().includes(s))
      );
    }

    // Filter by selected batch
    if (selectedBatch) {
      filtered = filtered.filter(
        (r) =>
          String(r.admittedbatch_id || r.admitted_batch_id || r.batch?.admittedbatch_id) ===
          String(selectedBatch)
      );
    }

    // Filter by selected branch
    if (selectedBranch) {
      filtered = filtered.filter(
        (r) => String(r.branch_id || r.branch?.branch_id) === String(selectedBranch)
      );
    }

    // Filter by admission type (multi-select)
    if (selectedAdmissionTypes && selectedAdmissionTypes.length > 0) {
      filtered = filtered.filter((r) =>
        selectedAdmissionTypes.includes((r.admission_type || "" || "").toString())
      );
    }

    // Filter by student status (DB column: student_status) (multi-select)
    if (selectedStudentStatuses && selectedStudentStatuses.length > 0) {
      filtered = filtered.filter((r) =>
        selectedStudentStatuses.includes((r.student_status || "" || "").toString())
      );
    }

    // Filter by enroll status (DB column: enroll_status) (multi-select)
    if (selectedEnrollStatuses && selectedEnrollStatuses.length > 0) {
      filtered = filtered.filter((r) =>
        selectedEnrollStatuses.includes((r.enroll_status || "" || "").toString())
      );
    }

    // Filter by workflow status (multi-select)
    if (selectedWorkflowStatuses && selectedWorkflowStatuses.length > 0) {
      filtered = filtered.filter((r) =>
        selectedWorkflowStatuses.includes((r.workflow_status || "" || "").toString())
      );
    }

    setRows(filtered);
  }, [
    search,
    allRows,
    selectedBatch,
    selectedBranch,
    selectedAdmissionTypes,
    selectedStudentStatuses,
    selectedEnrollStatuses,
    selectedWorkflowStatuses,
  ]);

  // Define columns for DataGrid
  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", flex: 0.5, minWidth: 60 },
      { field: "admission_no", headerName: "Admission No", flex: 1, minWidth: 120 },
      { field: "student_rollno", headerName: "Roll No", flex: 1, minWidth: 100 },
      { field: "student_name", headerName: "Student Name", flex: 1.5, minWidth: 160 },
      {
        field: "dob",
        headerName: "DOB",
        flex: 0.9,
        minWidth: 110,
        valueFormatter: (params) => (params.value ? String(params.value).split("T")[0] : ""),
      },
      { field: "gender", headerName: "Gender", flex: 0.7, minWidth: 80 },
      {
        field: "branch",
        headerName: "Branch",
        flex: 1.2,
        minWidth: 140,
        valueGetter: (params) => params.row.branch?.branch_name || params.row.branch_name || "",
      },
      {
        field: "batch",
        headerName: "Admitted Batch",
        flex: 1.2,
        minWidth: 140,
        valueGetter: (params) =>
          params.row.admittedbatch?.admittedbatch || params.row.admittedbatch || "",
      },
      { field: "admission_type", headerName: "Admission Type", flex: 1, minWidth: 120 },
      { field: "enroll_status", headerName: "Enroll Status", flex: 1, minWidth: 120 },
      { field: "email", headerName: "Email", flex: 1.6, minWidth: 180 },
      { field: "mobile", headerName: "Mobile", flex: 1, minWidth: 120 },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        flex: 0.9,
        minWidth: 120,
        renderCell: (params) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => showSnackbar("Row action not implemented yet", "info")}
          >
            Actions
          </Button>
        ),
      },
    ],
    []
  );

  // Filter handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleBatchSelect = (batchId) => {
    setSelectedBatch((prev) => (prev === batchId ? "" : batchId));
  };

  const handleToggleAdmissionType = (type) => {
    setSelectedAdmissionTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleToggleStudentStatus = (status) => {
    setSelectedStudentStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handleToggleEnrollStatus = (status) => {
    setSelectedEnrollStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handleToggleWorkflowStatus = (status) => {
    setSelectedWorkflowStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handleStudentStatusToggle = (status) => {
    // legacy single-select toggle removed; keep for backward compatibility but use multi-select below
    setSelectedStudentStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const clearAllFilters = () => {
    setSelectedBatch("");
    setSelectedBranch("");
    setSelectedAdmissionTypes([]);
    setSelectedStudentStatuses([]);
    setSelectedEnrollStatuses([]);
    setSelectedWorkflowStatuses([]);
  };

  // Helper lists for filters
  const uniqueBatches = batches.map((b) => b.admittedbatch).filter(Boolean);

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            STUDENT ADMISSION SUMMARY REPORT
          </Typography>
          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
            onClick={handleMenuOpen}
          >
            ACTIONS
          </Button>

          {/* Filter Button */}
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            sx={{
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                borderColor: "#115293",
                color: "#115293",
              },
            }}
            onClick={handleFilterClick}
          >
            FILTERS
            {(selectedBatch ||
              selectedBranch ||
              selectedAdmissionTypes.length ||
              selectedStudentStatuses.length ||
              selectedEnrollStatuses.length ||
              selectedWorkflowStatuses.length) && (
              <Chip
                size="small"
                label={
                  (selectedBatch ? 1 : 0) +
                  (selectedBranch ? 1 : 0) +
                  (selectedAdmissionTypes ? selectedAdmissionTypes.length : 0) +
                  (selectedStudentStatuses ? selectedStudentStatuses.length : 0) +
                  (selectedEnrollStatuses ? selectedEnrollStatuses.length : 0) +
                  (selectedWorkflowStatuses ? selectedWorkflowStatuses.length : 0)
                }
                sx={{ ml: 1, backgroundColor: "#1976d2", color: "white" }}
              />
            )}
          </Button>
          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{
                  backgroundColor: "#4caf50",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#357a38" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadCsvReport();
                }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                sx={{
                  backgroundColor: "#e53935",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#c62828" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleDownloadPdfReport();
                }}
              >
                DOWNLOAD PDF
              </Button>
            </MenuItem>
            {/* Student enrollment health downloads removed */}
          </Menu>

          {/* Filter Popover */}
          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                background: "#ffffff",
                minWidth: 240,
                maxWidth: 280,
              },
            }}
          >
            <Box
              sx={{
                p: 1.5,
                background: "#ffffff",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                  pb: 1,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#1976d2",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  Filters
                </Typography>
                <Button
                  size="small"
                  onClick={clearAllFilters}
                  sx={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    minWidth: "auto",
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: "#ffebee",
                    },
                  }}
                >
                  Clear
                </Button>
              </Box>

              {/* Admitted Batch Dropdown */}
              {/* Branch Dropdown (selecting branch filters admitted batches) */}
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Branch
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedBranch || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedBranch(val);
                    // clear selected batch when branch changes
                    setSelectedBatch("");
                  }}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Branches --</option>
                  {branches.map((b) => (
                    <option key={b.branch_id} value={b.branch_id}>
                      {b.branch_name}
                    </option>
                  ))}
                </TextField>
              </Box>

              {/* Admitted Batch Dropdown */}
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Admitted Batch
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedBatch || ""}
                  onChange={(e) => setSelectedBatch(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Batches --</option>
                  {batches.map((batch) => (
                    <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
                      {batch.admittedbatch}
                    </option>
                  ))}
                </TextField>
              </Box>

              <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

              {/* Admission Type Filter */}
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Admission Type
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: 0.5,
                    border: "1px solid #e9ecef",
                    maxHeight: 120,
                    overflowY: "auto",
                  }}
                >
                  <List dense sx={{ py: 0 }}>
                    {admissionTypes.map((type) => (
                      <ListItem key={type} disablePadding>
                        <ListItemButton
                          onClick={() => handleToggleAdmissionType(type)}
                          sx={{ py: 0.25, px: 0.5, minHeight: 28 }}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedAdmissionTypes.includes(type)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                            sx={{ color: "#1976d2", p: 0.25 }}
                          />
                          <ListItemText
                            primary={type}
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.8rem",
                                color: "#555",
                                fontWeight: selectedAdmissionTypes.includes(type) ? 600 : 400,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>

              {/* Enroll Status Filter */}
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Enroll Status
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: 0.5,
                    border: "1px solid #e9ecef",
                    maxHeight: 120,
                    overflowY: "auto",
                  }}
                >
                  <List dense sx={{ py: 0 }}>
                    {enrollStatuses.map((status) => (
                      <ListItem key={status} disablePadding>
                        <ListItemButton
                          onClick={() => handleToggleEnrollStatus(status)}
                          sx={{ py: 0.25, px: 0.5, minHeight: 28 }}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedEnrollStatuses.includes(status)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                            sx={{ color: "#1976d2", p: 0.25 }}
                          />
                          <ListItemText
                            primary={status}
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.8rem",
                                color: "#555",
                                fontWeight: selectedEnrollStatuses.includes(status) ? 600 : 400,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>

              {/* Workflow Status Filter */}
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Workflow Status
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: 0.5,
                    border: "1px solid #e9ecef",
                    maxHeight: 120,
                    overflowY: "auto",
                  }}
                >
                  <List dense sx={{ py: 0 }}>
                    {workflowStatuses.map((status) => (
                      <ListItem key={status} disablePadding>
                        <ListItemButton
                          onClick={() => handleToggleWorkflowStatus(status)}
                          sx={{ py: 0.25, px: 0.5, minHeight: 28 }}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedWorkflowStatuses.includes(status)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                            sx={{ color: "#1976d2", p: 0.25 }}
                          />
                          <ListItemText
                            primary={status}
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.8rem",
                                color: "#555",
                                fontWeight: selectedWorkflowStatuses.includes(status) ? 600 : 400,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>

              {/* Student Status Filter */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Student Status
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: 0.5,
                    border: "1px solid #e9ecef",
                    maxHeight: 120,
                    overflowY: "auto",
                  }}
                >
                  <List dense sx={{ py: 0 }}>
                    {studentStatuses.map((status) => (
                      <ListItem key={status} disablePadding>
                        <ListItemButton
                          onClick={() => handleToggleStudentStatus(status)}
                          sx={{ py: 0.25, px: 0.5, minHeight: 28 }}
                        >
                          <Checkbox
                            edge="start"
                            checked={selectedStudentStatuses.includes(status)}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                            sx={{ color: "#1976d2", p: 0.25 }}
                          />
                          <ListItemText
                            primary={status}
                            sx={{
                              "& .MuiListItemText-primary": {
                                fontSize: "0.8rem",
                                color: "#555",
                                fontWeight: selectedStudentStatuses.includes(status) ? 600 : 400,
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </Box>
          </Popover>

          <TextField
            size="small"
            placeholder="Search data"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => fetchData()}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Box>
      </Paper>

      {/* Staff management dialog removed — component now focuses on student reports */}

      <Footer />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
